import { useState, useEffect, useRef, useCallback } from 'react';
import { settingsApi, adminApi } from '@/services/api';

interface MediaFile {
  id: string;
  name: string;
  url: string;
  size: string;
  type: string;
  category: string;
  uploadedAt: string;
}

interface MediaPickerModalProps {
  onSelect: (url: string) => void;
  onClose: () => void;
}

const SEED_AVATARS: MediaFile[] = [
  { id: 'av1', name: 'avatar_rajesh.jpg', url: 'https://readdy.ai/api/search-image?query=professional%20headshot%20Indian%20male%20engineer%2040s%20confident%20smile%20office%20background%20clean%20portrait&width=200&height=200&seq=av1&orientation=squarish', size: '85 KB', type: 'image/jpeg', category: 'avatars', uploadedAt: new Date().toISOString() },
  { id: 'av2', name: 'avatar_priya.jpg', url: 'https://readdy.ai/api/search-image?query=professional%20headshot%20Indian%20female%20manager%2030s%20confident%20smile%20office%20background%20clean%20portrait&width=200&height=200&seq=av2&orientation=squarish', size: '78 KB', type: 'image/jpeg', category: 'avatars', uploadedAt: new Date().toISOString() },
];

async function compressImage(file: File): Promise<File> {
  if (!file.type.startsWith('image/') || file.type === 'image/svg+xml') return file;
  return new Promise<File>((resolve) => {
    const img = new Image();
    const blobUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(blobUrl);
      let { width, height } = img;
      const maxW = 400;
      if (width > maxW) { height = Math.round((height / width) * maxW); width = maxW; }
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) { resolve(file); return; }
      ctx.drawImage(img, 0, 0, width, height);
      const mime = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
      canvas.toBlob((blob) => {
        if (!blob || blob.size >= file.size) { resolve(file); return; }
        const ext = mime === 'image/jpeg' ? '.jpg' : '.png';
        resolve(new File([blob], file.name.replace(/\.[^.]+$/, '') + ext, { type: mime }));
      }, mime, 0.85);
    };
    img.onerror = () => { URL.revokeObjectURL(blobUrl); resolve(file); };
    img.src = blobUrl;
  });
}

export default function MediaPickerModal({ onSelect, onClose }: MediaPickerModalProps) {
  const [tab, setTab] = useState<'library' | 'upload'>('library');
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState<'all' | 'avatars' | 'logos' | 'job_photos' | 'site_visits'>('all');
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  // Upload state
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadPreview, setUploadPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setLoading(true);
    adminApi.getMedia({ limit: 200 } as Parameters<typeof adminApi.getMedia>[0])
      .then((res: { data?: Record<string, unknown>[] }) => {
        const rows = res?.data ?? [];
        if (rows.length > 0) {
          setMediaFiles(rows
            .filter((r) => {
              const t = String(r.type || '');
              const u = String(r.url || '');
              return t.startsWith('image/') || /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(u);
            })
            .map((r) => ({
              id: String(r.id),
              name: String(r.original_name || r.name || ''),
              url: String(r.url),
              size: String(r.size || '—'),
              type: String(r.type || 'image/jpeg'),
              category: String(r.category || 'documents'),
              uploadedAt: String(r.uploaded_at || r.created_at || new Date().toISOString()),
            })));
        } else {
          setMediaFiles(SEED_AVATARS);
        }
      })
      .catch(() => setMediaFiles(SEED_AVATARS))
      .finally(() => setLoading(false));
  }, []);

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError('');
    if (file.size > 2 * 1024 * 1024) { setUploadError('File too large. Max 2MB.'); return; }
    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) { setUploadError('Only PNG, JPG, WEBP, GIF allowed.'); return; }
    const compressed = await compressImage(file);
    setUploadFile(compressed);
    const reader = new FileReader();
    reader.onload = (ev) => setUploadPreview(ev.target?.result as string);
    reader.readAsDataURL(compressed);
    e.target.value = '';
  }, []);

  const handleUploadAndSelect = async () => {
    if (!uploadFile) return;
    setUploading(true);
    setUploadError('');
    try {
      // Upload to media library first
      const fd = new FormData();
      fd.append('file', uploadFile, uploadFile.name);
      fd.append('category', 'avatars');
      const result = await settingsApi.uploadMediaFile(fd);
      const url = result?.url || uploadPreview || '';
      if (!url) throw new Error('No URL returned');
      onSelect(url);
    } catch (err) {
      // If media upload fails, try direct avatar upload
      try {
        const avatarResult = await settingsApi.uploadAvatar(uploadFile);
        const url = avatarResult?.avatar_url || '';
        if (!url) throw new Error('No URL returned');
        onSelect(url);
      } catch {
        setUploadError('Upload failed. Please try again.');
      }
    } finally {
      setUploading(false);
    }
  };

  const filtered = mediaFiles.filter((f) => {
    if (filterCat !== 'all' && f.category !== filterCat) return false;
    if (search && !f.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const catOptions = [
    { key: 'all', label: 'All Images', icon: 'ri-apps-2-line' },
    { key: 'avatars', label: 'Avatars', icon: 'ri-user-3-line' },
    { key: 'logos', label: 'Logos', icon: 'ri-image-line' },
    { key: 'job_photos', label: 'Job Photos', icon: 'ri-camera-line' },
    { key: 'site_visits', label: 'Site Visits', icon: 'ri-map-pin-2-line' },
  ] as const;

  return (
    <div className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: '#f5f3ff' }}>
              <i className="ri-image-2-line text-base" style={{ color: '#7c3aed' }} />
            </div>
            <div>
              <h3 className="font-bold text-gray-900 text-sm">Pick Profile Photo</h3>
              <p className="text-xs text-gray-400">Choose from media library or upload a new image</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-gray-100 cursor-pointer transition-all">
            <i className="ri-close-line text-gray-500 text-lg" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-100 flex-shrink-0 px-5">
          {[
            { key: 'library', label: 'Media Library', icon: 'ri-gallery-line' },
            { key: 'upload', label: 'Upload New', icon: 'ri-upload-2-line' },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key as 'library' | 'upload')}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all cursor-pointer whitespace-nowrap -mb-px ${
                tab === t.key
                  ? 'border-violet-500 text-violet-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className={`${t.icon} text-sm`} />
              </span>
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">

          {/* ── Library Tab ── */}
          {tab === 'library' && (
            <div className="p-5 space-y-4">
              {/* Search + filter */}
              <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 min-w-[160px]">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                    <i className="ri-search-line text-gray-400 text-sm" />
                  </span>
                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search images..."
                    className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-400"
                  />
                </div>
                <div className="flex gap-1.5 flex-wrap">
                  {catOptions.map((c) => (
                    <button
                      key={c.key}
                      onClick={() => setFilterCat(c.key)}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-xs font-medium border cursor-pointer transition-all whitespace-nowrap ${
                        filterCat === c.key
                          ? 'border-violet-400 text-violet-700'
                          : 'border-gray-200 text-gray-500 hover:border-gray-300'
                      }`}
                      style={filterCat === c.key ? { background: '#f5f3ff' } : {}}
                    >
                      <i className={`${c.icon} text-xs`} />
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Grid */}
              {loading ? (
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-3">
                  {Array.from({ length: 10 }).map((_, i) => (
                    <div key={i} className="rounded-xl bg-gray-100 animate-pulse" style={{ aspectRatio: '1' }} />
                  ))}
                </div>
              ) : filtered.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-gray-100 mx-auto mb-3">
                    <i className="ri-image-line text-2xl" />
                  </div>
                  <p className="text-sm font-medium text-gray-600">No images found</p>
                  <p className="text-xs mt-1">
                    {search ? `No results for "${search}"` : 'Upload images to the media library first'}
                  </p>
                  <button
                    onClick={() => setTab('upload')}
                    className="mt-3 px-4 py-2 text-white text-xs rounded-xl font-semibold cursor-pointer transition-all"
                    style={{ background: '#7c3aed' }}
                  >
                    Upload New Image
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-3">
                  {filtered.map((f) => (
                    <div
                      key={f.id}
                      className="relative rounded-xl overflow-hidden border-2 cursor-pointer transition-all group"
                      style={{
                        aspectRatio: '1',
                        borderColor: hoveredId === f.id ? '#7c3aed' : '#e5e7eb',
                      }}
                      onMouseEnter={() => setHoveredId(f.id)}
                      onMouseLeave={() => setHoveredId(null)}
                      onClick={() => onSelect(f.url)}
                    >
                      <img
                        src={f.url}
                        alt={f.name}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).src =
                            "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect width='100' height='100' fill='%23f3f4f6'/%3E%3Ctext x='50' y='55' text-anchor='middle' fill='%239ca3af' font-size='10'%3ENo Preview%3C/text%3E%3C/svg%3E";
                        }}
                      />
                      {/* Hover overlay */}
                      <div
                        className="absolute inset-0 flex flex-col items-center justify-center gap-1 transition-all"
                        style={{ background: hoveredId === f.id ? 'rgba(124,58,237,0.75)' : 'transparent' }}
                      >
                        {hoveredId === f.id && (
                          <>
                            <div className="w-8 h-8 flex items-center justify-center bg-white rounded-full">
                              <i className="ri-check-line text-violet-600 text-base" />
                            </div>
                            <span className="text-white text-xs font-semibold">Select</span>
                          </>
                        )}
                      </div>
                      {/* Name tooltip */}
                      <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs px-1.5 py-1 truncate opacity-0 group-hover:opacity-100 transition-opacity">
                        {f.name}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <p className="text-xs text-gray-400 text-center">
                {filtered.length} image{filtered.length !== 1 ? 's' : ''} available · Click any image to use it as your profile photo
              </p>
            </div>
          )}

          {/* ── Upload Tab ── */}
          {tab === 'upload' && (
            <div className="p-5 space-y-4">
              <p className="text-xs text-gray-500">
                Upload a new image — it will be saved to the media library and set as your profile photo.
              </p>

              {uploadError && (
                <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                  <i className="ri-error-warning-line flex-shrink-0" />
                  {uploadError}
                </div>
              )}

              {/* Drop zone */}
              {!uploadPreview ? (
                <div
                  className="border-2 border-dashed border-gray-200 rounded-2xl p-10 text-center cursor-pointer hover:border-violet-300 hover:bg-violet-50/20 transition-all"
                  onClick={() => fileInputRef.current?.click()}
                  onDrop={(e) => {
                    e.preventDefault();
                    const file = e.dataTransfer.files?.[0];
                    if (file) {
                      const dt = new DataTransfer();
                      dt.items.add(file);
                      const fakeEvent = { target: { files: dt.files, value: '' } } as unknown as React.ChangeEvent<HTMLInputElement>;
                      handleFileChange(fakeEvent);
                    }
                  }}
                  onDragOver={(e) => e.preventDefault()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                  <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-violet-50 mx-auto mb-4">
                    <i className="ri-upload-cloud-2-line text-3xl" style={{ color: '#7c3aed' }} />
                  </div>
                  <p className="text-sm font-semibold text-gray-700">Click or drag &amp; drop your photo</p>
                  <p className="text-xs text-gray-400 mt-1.5">PNG, JPG, WEBP, GIF · Max 2MB</p>
                  <p className="text-xs text-gray-400 mt-0.5">Image will be auto-compressed to 400px max width</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Preview */}
                  <div className="flex items-center gap-5">
                    <div className="relative flex-shrink-0">
                      <img
                        src={uploadPreview}
                        alt="Preview"
                        className="w-28 h-28 rounded-full object-cover border-4 border-violet-100"
                      />
                      <div className="absolute -bottom-1 -right-1 w-7 h-7 flex items-center justify-center bg-amber-500 rounded-full border-2 border-white">
                        <i className="ri-check-line text-white text-xs" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-gray-800">{uploadFile?.name}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {uploadFile ? `${(uploadFile.size / 1024).toFixed(1)} KB` : ''} · Ready to upload
                      </p>
                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={() => { setUploadFile(null); setUploadPreview(null); setUploadError(''); }}
                          className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 text-gray-600 rounded-xl text-xs font-medium cursor-pointer hover:bg-gray-50 transition-all whitespace-nowrap"
                        >
                          <i className="ri-refresh-line" />
                          Change
                        </button>
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 text-gray-600 rounded-xl text-xs font-medium cursor-pointer hover:bg-gray-50 transition-all whitespace-nowrap"
                        >
                          <i className="ri-folder-open-line" />
                          Browse
                        </button>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                          className="hidden"
                          onChange={handleFileChange}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Preview as avatar sizes */}
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-xs font-semibold text-gray-600 mb-3">Preview at different sizes</p>
                    <div className="flex items-end gap-4">
                      {[
                        { size: 64, label: 'Sidebar' },
                        { size: 40, label: 'Header' },
                        { size: 28, label: 'Small' },
                      ].map(({ size, label }) => (
                        <div key={size} className="flex flex-col items-center gap-1.5">
                          <img
                            src={uploadPreview}
                            alt="preview"
                            className="rounded-full object-cover border-2 border-white"
                            style={{ width: size, height: size, flexShrink: 0 }}
                          />
                          <span className="text-xs text-gray-400">{label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Upload button */}
              {uploadPreview && (
                <button
                  onClick={handleUploadAndSelect}
                  disabled={uploading || !uploadFile}
                  className="w-full flex items-center justify-center gap-2 text-white py-3 rounded-xl text-sm font-bold cursor-pointer transition-all whitespace-nowrap disabled:opacity-60"
                  style={{ background: '#7c3aed' }}
                >
                  {uploading ? (
                    <><i className="ri-loader-4-line animate-spin" />Uploading to media library...</>
                  ) : (
                    <><i className="ri-upload-2-line" />Upload &amp; Set as Profile Photo</>
                  )}
                </button>
              )}

              <div className="flex items-center gap-2 text-xs text-gray-400">
                <i className="ri-information-line flex-shrink-0" />
                The image will be saved to the Media Library under &quot;Avatars&quot; category and set as your profile photo.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
